import { useEffect, useMemo, useState } from "react";
import { useGlobalStacks } from "@/state/globalStacks";
import {
  useGetCollectionQuery,
  useGetCollectionsQuery,
  useGetDBCountsQuery,
  useGetRecordQuery,
  useGetUserQuery,
  useLazyCheckNicknameQuery,
} from "./api";
import { deserializeRecord } from "@/common/record";
import { HGAnyRecord, HGCollection, HGUser } from "@/common/types";
import { useAppSelector } from "@/state/redux/hooks";
import { selectMe } from "@/state/redux/slices/ui";
import { useLocation, useNavigate, useParams } from "react-router";
import {
  MAX_COLLECTION_SLUG_LENGTH,
  NICKNAME_MESSAGES,
  checkNickname,
  normalizeSlug,
} from "@/common/slug";

export function useCurrentUserID() {
  const { userID } = useParams();
  const me = useAppSelector(selectMe);

  return userID || me?.id;
}

/**
 * Whether the signed-in user is an admin.
 *
 * Synchronous and correct on first render: main.tsx awaits
 * checkAuthorization before mounting the router, so `me` is already
 * populated. As with useCanEditRecord, this only hides affordances -- every
 * /admin/* endpoint re-checks is_admin server-side on each request, read
 * fresh from user_account rather than from a cached session payload.
 */
export function useIsAdmin() {
  return !!useAppSelector(selectMe)?.is_admin;
}

/**
 * Whether the URL points at the signed-in user's own collections.
 *
 * The `:userID` segment is a *ref* — a nickname when the user has one, the raw
 * id otherwise — so it cannot be compared to `me.id` alone. Both forms are
 * accepted here, and so is the bare `/`: the collections endpoint falls back to
 * the session user when no id is given, which makes `/` and `/{me}` the same
 * space.
 *
 * Deliberately synchronous. Deriving this from a fetched user would make the
 * pinned group and the create-collection action blink on every navigation.
 */
export function useIsOwnSpace() {
  const { userID } = useParams();
  const me = useAppSelector(selectMe);

  if (!userID) return !!me;
  return !!me && (userID === me.id || userID === me.nickname.nickname);
}

/**
 * Whose collections are being browsed, and who that is when it is not you.
 *
 * Side-effect free, so several components can ask without fighting each other.
 * The canonical-URL redirect for a user page is issued once, by `Records`,
 * alongside the collection one.
 */
export function useSpace(): { isOwn: boolean; owner: HGUser | undefined } {
  const { userID } = useParams();
  const isOwn = useIsOwnSpace();
  const skip = !userID || isOwn;
  const { data: owner } = useGetUserQuery(userID || "", { skip });

  // `skip` stops the request but does not clear `data`: RTK Query keeps handing
  // back the last user it resolved. Leaving someone's space for "/" would
  // otherwise still report them as the owner -- which is not merely stale, it
  // is wrong, "/" having no owner at all. (Retention is deliberate *within* a
  // space: moving between two people keeps the old name on screen instead of
  // blanking it while the next lookup is out.)
  return { isOwn, owner: skip ? undefined : owner };
}

/**
 * The face of the space being browsed: what to draw an avatar with.
 *
 * Own name and picture come from `me`, i.e. from the API. They used to come
 * from the decoded Google ID token in localStorage, which is empty for a
 * password account. `name` is the person, never a label — deriving initials
 * from "Your collections" would put "YC" on an unnamed account.
 */
export function useSpaceIdentity(): {
  name: string | null | undefined;
  picture: string | null | undefined;
} {
  const me = useAppSelector(selectMe);
  const { isOwn, owner } = useSpace();

  if (isOwn) {
    return {
      name: me?.name || me?.nickname?.nickname || me?.id,
      picture: me?.picture,
    };
  }
  return { name: owner?.name || owner?.nickname, picture: owner?.picture };
}

/**
 * The id of the user whose space is on screen: the signed-in user in one's own
 * space, the resolved owner in someone else's — falling back to the raw URL ref
 * while that lookup is still out, so a first request goes out immediately
 * rather than waiting on it.
 *
 * Not `useCurrentUserID`, which hands back whatever the URL segment says. A
 * query keyed on a ref opens a second cache entry for the same user the moment
 * the canonical redirect rewrites /{id} to /{nickname}, and every request under
 * the old spelling is then made again under the new one.
 */
export function useSpaceUserID() {
  const { userID } = useParams();
  const me = useAppSelector(selectMe);
  const { isOwn, owner } = useSpace();

  return isOwn ? me?.id : (owner?.id ?? userID);
}

const NO_COLLECTIONS: HGCollection[] = [];

/**
 * Returned array is reference-stable for as long as the query data and the
 * requested ids are: it is a dependency of memoized record-tree builds, and a
 * fresh array every render would rebuild the whole tree.
 */
export function useCollections(collectionIDs?: string[]) {
  const { userID } = useParams();
  const { data: collections } = useGetCollectionsQuery(userID);
  const idsKey = collectionIDs ? collectionIDs.join(",") : null;

  return useMemo(() => {
    if (!collections) return NO_COLLECTIONS;
    if (idsKey === null) return collections;
    const ids = idsKey === "" ? [] : idsKey.split(",");
    return collections.filter((collection: HGCollection) =>
      ids.includes(collection.id),
    );
  }, [collections, idsKey]);
}

/**
 * The collections a single record belongs to.
 *
 * `index` is the escape hatch for lists: a caller rendering thousands of rows
 * builds `id → collection` once and hands it down, and the query subscription
 * here is skipped entirely. Without it every row opened its own subscription
 * and filtered the whole collection list for itself, which is O(rows ×
 * collections) of work repeated on every render.
 */
export function useRecordCollections(
  collectionIDs: string[],
  index?: Map<string, HGCollection>,
): HGCollection[] {
  const { userID } = useParams();
  const { data: collections } = useGetCollectionsQuery(userID, {
    skip: !!index,
  });
  const idsKey = collectionIDs.join(",");

  return useMemo(() => {
    const ids = idsKey === "" ? [] : idsKey.split(",");
    if (index) {
      const resolved = ids
        .map((id) => index.get(id))
        .filter((collection): collection is HGCollection => !!collection);
      return resolved.length > 0 ? resolved : NO_COLLECTIONS;
    }
    if (!collections) return NO_COLLECTIONS;
    return collections.filter((collection: HGCollection) =>
      ids.includes(collection.id),
    );
  }, [collections, idsKey, index]);
}

/**
 * The signed-in user's own collections, whatever space is being browsed.
 *
 * `useCollections` keys off the `:userID` segment, which is what list views
 * want. Anything that writes must not: a record can only be filed into a
 * collection you own, and the API rejects the rest with a 404. Empty when
 * signed out.
 */
export function useMyCollections() {
  const me = useAppSelector(selectMe);
  // No user_id: the endpoint falls back to the session user. Passing me.id
  // instead would split the cache whenever the URL carries a nickname.
  const { data: collections } = useGetCollectionsQuery(undefined, {
    skip: !me,
  });

  return collections ?? NO_COLLECTIONS;
}

/**
 * Whether the signed-in user may edit `record`.
 *
 * A record with no `user_id` has not been created yet, so it is the caller's by
 * definition. The API is the one enforcing this -- every record write is scoped
 * to the session user -- but an edit affordance that can only 404 should not be
 * on screen.
 */
export function useCanEditRecord(
  // Structural rather than HGAnyRecord: callers hold the record in every shape
  // it comes in (serialized, deserialized, still-unsaved form data), and the
  // only field that matters is present in all of them but the last.
  record: object | null | undefined,
) {
  const me = useAppSelector(selectMe);

  if (!me || !record) return false;
  if (!("user_id" in record)) return true;
  return (record as { user_id?: string }).user_id === me.id;
}

export function usePinnedCollections() {
  const collections = useCollections();
  const me = useAppSelector(selectMe);
  const isOwn = useIsOwnSpace();
  const slots = me?.quotas.pinned_slots ?? 0;

  // `pin_position` comes back for anyone's collections, so on a foreign page
  // this would otherwise list *their* pins and compute the cap from them.
  // Pinning is personal: outside your own space there is nothing to show and
  // nothing to pin.
  if (!isOwn) return { pinned: NO_COLLECTIONS, slots, atCap: true };

  const pinned = collections
    .filter((collection) => collection.pin_position !== null)
    .sort((a, b) => (a.pin_position ?? 0) - (b.pin_position ?? 0));

  return { pinned, slots, atCap: pinned.length >= slots };
}

export function useCollectionBase() {
  const userID = useCurrentUserID();
  const { collectionID } = useParams();
  const {
    data: collection,
    isLoading,
    isFetching,
  } = useGetCollectionQuery(
    {
      userID: userID || "",
      collectionID: collectionID || "",
    },
    {
      skip: !userID || !collectionID,
    },
  );
  if (!collectionID)
    return { collection: undefined, isLoading: false, isFetching: false };
  return {
    collection,
    isLoading,
    isFetching,
  };
}

export function useCollection() {
  const { collection } = useCollectionBase();
  return collection;
}

export function useCollectionFetching() {
  const { isFetching } = useCollectionBase();
  return isFetching;
}

/**
 * Send the browser to the canonical URL when the one it used was stale.
 *
 * The API returns `canonical_path` in the body rather than issuing a 301,
 * because a redirect on an XHR is invisible to the router and would leave the
 * address bar showing the old URL.
 */
export function useCanonicalPath(canonicalPath: string | null | undefined) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!canonicalPath) return;
    if (canonicalPath === location.pathname) return;
    // replace, not push: the stale URL should not sit in the back stack.
    navigate(canonicalPath + location.search, {
      replace: true,
      state: location.state,
    });
  }, [
    canonicalPath,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);
}

export type SlugCheck = {
  normalized: string;
  state: "idle" | "checking" | "ok" | "error";
  message: string | null;
};

/**
 * Whether a collection URL name is free.
 *
 * Checked entirely on the client: the sidebar already holds every collection
 * of the current user, so there is nothing to ask the server. A save can still
 * lose a race, which the API answers with 409.
 */
export function useCollectionSlugCheck(
  value: string,
  excludeID: string,
): SlugCheck {
  const collections = useCollections();

  return useMemo(() => {
    const normalized = normalizeSlug(value, MAX_COLLECTION_SLUG_LENGTH);
    if (!normalized) {
      return {
        normalized,
        state: "error" as const,
        message: "Name must contain at least one letter or digit.",
      };
    }
    const clash = collections.some(
      (collection) =>
        collection.id !== excludeID &&
        (collection.slug === normalized || collection.id === normalized),
    );
    if (clash) {
      return {
        normalized,
        state: "error" as const,
        message: "You already have a collection with that URL name.",
      };
    }
    return { normalized, state: "ok" as const, message: null };
  }, [value, excludeID, collections]);
}

const NICKNAME_DEBOUNCE_MS = 400;

/** Debounced nickname availability check. */
export function useNicknameCheck(
  value: string,
  current: string | null,
): SlugCheck {
  const [trigger] = useLazyCheckNicknameQuery();
  const local = useMemo(() => checkNickname(value), [value]);

  // Cases decidable without a network round-trip: empty input, an
  // unnormalizable value, or a value that's already the current nickname.
  const syncResult: SlugCheck | null = useMemo(() => {
    if (!value) return { normalized: "", state: "idle", message: null };
    if (local.problem) {
      return {
        normalized: local.normalized,
        state: "error",
        message: NICKNAME_MESSAGES[local.problem],
      };
    }
    if (local.normalized === current) {
      return { normalized: local.normalized, state: "idle", message: null };
    }
    return null;
  }, [value, local, current]);

  const [asyncResult, setAsyncResult] = useState<SlugCheck | null>(null);

  useEffect(() => {
    if (syncResult) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await trigger(local.normalized).unwrap();
        if (cancelled) return;
        setAsyncResult({
          normalized: response.normalized || local.normalized,
          state: response.available ? "ok" : "error",
          message: response.available
            ? null
            : response.message ||
              NICKNAME_MESSAGES[response.reason || ""] ||
              "That nickname is not available.",
        });
      } catch {
        if (cancelled) return;
        // A failed check must not block the user; the save is authoritative.
        setAsyncResult({
          normalized: local.normalized,
          state: "idle",
          message: null,
        });
      }
    }, NICKNAME_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [syncResult, local.normalized, trigger]);

  if (syncResult) return syncResult;
  if (asyncResult && asyncResult.normalized === local.normalized)
    return asyncResult;
  return { normalized: local.normalized, state: "checking", message: null };
}

export function useRecordsCount() {
  const collections = useCollections();
  const { userID } = useParams();
  const me = useAppSelector(selectMe);
  const globalStacks = useGlobalStacks();
  // rerenders twice on interval change:
  // 1. when globalStacks.interval changes, useGlobalStacks() triggers rerender
  // 2. when useGetDBCountsQuery() is called with new interval, it triggers rerender when data is fetched
  // without selectFromResult it would rerender 3 times: pending, pending with additional metadata (like requestID), fullfilled
  const counts: Record<string, number> = useGetDBCountsQuery(
    {
      userID: userID || me?.id,
      interval: globalStacks.getIntervalAPI(),
      endInterval: globalStacks.getEndIntervalAPI(),
    },
    {
      selectFromResult: ({ data }) => data || {}, // trigger rerender only when data changes
    },
  );

  if (!collections || !counts || Object.keys(counts).length === 0) return {};

  return counts;

  // if I want to count children collections records for parent collection
  // const calculatedCollections: Record<string, number> = {};

  // const collectionByID: Record<string, HGCollection> = {};
  // for (const collection of collections) {
  //   collectionByID[collection.id] = collection;
  // }

  // const getCollectionCount = (collectionID: string) => {
  //   if (collectionID in calculatedCollections) {
  //     return calculatedCollections[collectionID];
  //   } else if (collectionID in collectionByID) {
  //     const collection = collectionByID[collectionID];
  //     const count = collection.child_ids.reduce(
  //       (totalCount, childCollectionID) => {
  //         const returns: number =
  //           totalCount + getCollectionCount(childCollectionID);
  //         return returns;
  //       },
  //       counts[collectionID] || 0,
  //     );
  //     calculatedCollections[collectionID] = count;
  //     return count;
  //   } else return 0;
  // };

  // for (const collection of collections) {
  //   getCollectionCount(collection.id);
  // }

  // return Object.keys(calculatedCollections).reduce(
  //   (acc, collectionID) => {
  //     if (calculatedCollections[collectionID] > 0) {
  //       acc[collectionID] = calculatedCollections[collectionID];
  //     }
  //     return acc;
  //   },
  //   {} as Record<string, number>,
  // );
}

/**
 * A record with its full `content`.
 *
 * The timeline's records come from the bulk listing, which sends an excerpt --
 * two clamped lines is all a row renders, and the full text runs to fifty
 * thousand characters per record. Anything that shows the whole thing asks for
 * it here, and gets the excerpt to render in the meantime so opening a preview
 * is never a blank pane.
 */
export function useFullRecord(record: HGAnyRecord | null): HGAnyRecord | null {
  const needsFullText = !!record?.content_truncated;
  const { data } = useGetRecordQuery(record?.id ?? "", {
    skip: !record || !needsFullText,
  });

  return useMemo(() => {
    if (!record || !needsFullText) return record;
    const full = deserializeRecord(data ?? null);
    if (!full) return record;
    // Only the text is taken from the fetch: everything else on the row has
    // already been through the listing's enrichment (interval names, the
    // intersection with the viewed range), which the by-id response has not.
    return { ...record, content: full.content, content_truncated: false };
  }, [record, needsFullText, data]);
}
